import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const COST_PER_SECOND = 0.12 * 1_000_000 * (1 + BRAND_MARKUP);
const COST_PER_SECOND_HIGH = 0.25 * 1_000_000 * (1 + BRAND_MARKUP);

const REPLICATE_EXTEND_COST_PER_SECOND_HIGH =
  0.29 * 1_000_000 * (1 + BRAND_MARKUP);
const REPLICATE_EXTEND_COST_PER_SECOND_BASIC =
  0.22 * 1_000_000 * (1 + BRAND_MARKUP);

const seedance2ExtendParams = z
  .object({
    duration: z.enum(["5", "10", "15"]).default("5"),
    quality: z.enum(["basic", "high"]).default("basic"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const seedance2Extend = defineModel({
  key: "seedance-2-extend",
  stepType: "video",
  spec: {
    label: "Seedance 2.0 Extend",
    description: "Extend an existing Seedance 2.0 video seamlessly",
    icon: "seedance-icon.png",
    tags: ["video-extend"],
    featured: false,
    hidden: true,
    info: "Extends a previous Seedance 2.0 generation. Requires a prior Seedance video as input.",
    features: {
      duration: true,
      quality: true,
      attachments: true,
      prompts: true,
    } as const,
    options: {
      attachments: [
        {
          type: "video" as const,
          allowedFormats: ["video/mp4"],
          label: "Source Video",
          slot: "source_video",
          required: true,
        },
      ],
      duration: {
        items: [
          { label: "5s", value: "5" },
          { label: "10s", value: "10" },
          { label: "15s", value: "15" },
        ],
      },
      quality: {
        items: [
          { label: "Basic", value: "basic" },
          { label: "High", value: "high" },
        ],
      },
    },
    params: seedance2ExtendParams,
  },
  providers: [
    {
      provider: "replicate",
      modelId: "bytedance/seedance-2.0",
      estimateCost(params) {
        const duration = Number(params.duration) || 5;
        const perSecond =
          params.quality === "high"
            ? REPLICATE_EXTEND_COST_PER_SECOND_HIGH
            : REPLICATE_EXTEND_COST_PER_SECOND_BASIC;
        return Math.ceil(perSecond * duration);
      },
      costPerSecond: REPLICATE_EXTEND_COST_PER_SECOND_BASIC,
      toPayload(params) {
        const sourceVideoUrl = params.attachments?.find(
          (att) => att.slot === "source_video",
        )?.data;

        // Replicate's seedance-2.0 (Normal) Cog has a bug where reference_videos
        // fails. Extend always uses reference_videos, so force Fast regardless of
        // quality setting. Remove this override when Replicate fixes the Normal Cog.
        const model = "bytedance/seedance-2.0-fast" as const;

        return {
          model,
          input: {
            prompt: params.prompt || undefined,
            ...(sourceVideoUrl ? { reference_videos: [sourceVideoUrl] } : {}),
            resolution: "720p" as const,
            duration: Number(params.duration),
            aspect_ratio: "adaptive" as const,
            generate_audio: true,
          },
        };
      },
    },
    {
      provider: "muapi",
      modelId: "seedance-v2.0-extend",
      estimateCost(params) {
        const duration = Number(params.duration) || 5;
        const costPerSecond =
          params.quality === "high" ? COST_PER_SECOND_HIGH : COST_PER_SECOND;
        return Math.ceil(costPerSecond * duration);
      },
      costPerSecond: COST_PER_SECOND,
      toPayload(params) {
        return {
          endpoint: "seedance-v2.0-extend" as const,
          prompt: params.prompt || undefined,
          duration: Number(params.duration),
          quality: params.quality,
          remove_watermark: true,
        };
      },
    },
  ],
});
